import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AsistentePage = () => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const sid = `chat_${user?.id || 'anon'}_${Date.now()}`;
    setSessionId(sid);
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API}/api/asistente/sessions/${user.establecimiento_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* */ }
  };

  const loadHistory = async (sid) => {
    try {
      const res = await fetch(`${API}/api/asistente/history/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMessages((data.history || []).map(m => ({ role: m.role, content: m.content })));
      setSessionId(sid);
      setShowSessions(false);
    } catch { /* */ }
  };

  const handleNewChat = () => {
    const sid = `chat_${user?.id || 'anon'}_${Date.now()}`;
    setSessionId(sid);
    setMessages([]);
    setShowSessions(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/asistente/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
          establecimiento_id: user.establecimiento_id,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        let errMsg = 'Error del asistente.';
        try {
          const errData = await res.json();
          errMsg = errData.detail || errMsg;
        } catch { /* */ }
        setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error de conexion. Verifica tu internet e intenta de nuevo.' }]);
    }
    setLoading(false);
    loadSessions();
  };

  const handleDeleteSession = async (sid) => {
    try {
      await fetch(`${API}/api/asistente/session/${sid}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sid === sessionId) handleNewChat();
    } catch { /* */ }
  };

  const quickQuestions = [
    "Que vacas deberia descartar y por que?",
    "Como va mi rodeo este mes?",
    "Que toro tiene mejor fertilidad?",
    "Cuales son mis animales con mejor GMD?",
    "Dame un resumen de las alertas activas",
  ];

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="asistente-page">
      {/* Sessions sidebar */}
      <div className={`${showSessions ? 'block' : 'hidden'} md:block w-full md:w-64 bg-gray-50 border-r border-gray-200 flex-shrink-0 overflow-y-auto`}>
        <div className="p-3">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center gap-2 px-3 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors"
            data-testid="new-chat-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Nueva consulta
          </button>
        </div>
        <div className="px-3 pb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Historial</p>
          {sessions.length === 0 && <p className="text-xs text-gray-400 px-2">Sin conversaciones previas</p>}
          {sessions.map(s => (
            <div
              key={s.session_id}
              className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm mb-1 transition-colors ${s.session_id === sessionId ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100 text-gray-600'}`}
              onClick={() => loadHistory(s.session_id)}
            >
              <span className="truncate flex-1">{s.last_message}</span>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.session_id); }}
                className="opacity-0 group-hover:opacity-100 ml-2 text-gray-400 hover:text-red-500 transition-all"
                data-testid={`delete-session-${s.session_id}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="md:hidden p-1.5 rounded-lg hover:bg-gray-100"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            </button>
            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0110 10c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2z" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">Asistente TKorveX</h3>
              <p className="text-xs text-gray-400">Consultor tecnico ganadero IA</p>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded text-xs text-green-700 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Online
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Asistente Tecnico TKorveX</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md">
                Preguntame sobre tu rodeo: descartes, fertilidad, pesajes, recomendaciones de manejo, analisis de datos...
              </p>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); }}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all"
                    data-testid={`quick-q-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] md:max-w-[70%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-green-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
                data-testid={`msg-${msg.role}-${i}`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregunta sobre tu rodeo..."
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-xl transition-colors flex items-center gap-1.5"
              data-testid="send-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AsistentePage;
